import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, User, ArrowLeft, Lock, Eye, EyeOff, Bell, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fields that members can edit
const EDITABLE_FIELDS = [
  'Vorname',
  'Nachname',
  'E-Mail',
  'Telefon',
  'Tel. Privat',
  'Tel. Geschäft',
  'Tel. Mobile',
  'Mobile',
  'Handy',
  'Fax',
  'Strasse',
  'PLZ',
  'Ort',
  'Land'
];

export default function EditProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Newsletter subscription state
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [loadingNewsletter, setLoadingNewsletter] = useState(true);
  const [savingNewsletter, setSavingNewsletter] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/profile/me`);
        setProfile(response.data);
        
        // Extract editable fields
        const fields = response.data.fields || {};
        const editableData = {};
        
        for (const fieldName of EDITABLE_FIELDS) {
          // Find field by name (case-insensitive)
          for (const [key, value] of Object.entries(fields)) {
            if (key.toLowerCase() === fieldName.toLowerCase() || 
                key.toLowerCase().includes(fieldName.toLowerCase())) {
              const text = typeof value === 'object' ? value.text : value;
              editableData[key] = text || '';
              break;
            }
          }
        }
        
        setFormData(editableData);
        setOriginalData(editableData);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Profil konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    const fetchNewsletterStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/profile/newsletter`);
        setNewsletterSubscribed(response.data.newsletter_subscribed || false);
      } catch (error) {
        console.error('Failed to fetch newsletter status:', error);
      } finally {
        setLoadingNewsletter(false);
      }
    };

    fetchProfile();
    fetchNewsletterStatus();
  }, []);

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const hasChanges = () => {
    return Object.keys(formData).some(key => formData[key] !== originalData[key]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasChanges()) {
      toast.info('Keine Änderungen vorhanden');
      return;
    }
    
    setSaving(true);
    
    try {
      // Only send changed fields
      const changedFields = {};
      for (const [key, value] of Object.entries(formData)) {
        if (value !== originalData[key]) {
          changedFields[key] = value;
        }
      }
      
      const response = await axios.put(`${API_URL}/api/profile/me`, {
        fields: changedFields
      });
      
      toast.success(response.data.message);
      setOriginalData({...formData});
      
      // Show changes made
      if (response.data.changes) {
        response.data.changes.forEach(change => {
          console.log(`${change.field}: ${change.old} → ${change.new}`);
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordData.newPassword.length < 6) {
      toast.error('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success('Passwort erfolgreich geändert');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Passwort ändern fehlgeschlagen';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleNewsletterChange = async (checked) => {
    setSavingNewsletter(true);
    try {
      await axios.put(`${API_URL}/api/profile/newsletter`, {
        newsletter_subscribed: checked
      });
      setNewsletterSubscribed(checked);
      toast.success(checked ? 'Newsletter abonniert' : 'Newsletter abbestellt');
    } catch (error) {
      toast.error('Fehler beim Speichern der Newsletter-Einstellung');
    } finally {
      setSavingNewsletter(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-secondary/50" />
            <div className="bg-card border border-border p-8 space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-12 bg-secondary/50" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className=" animate-slide-in">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/members')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm uppercase tracking-wider"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} />
            Zurück
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-foreground uppercase tracking-tight mb-2">
            Mein Profil bearbeiten
          </h1>
          <p className="text-muted-foreground">
            Aktualisieren Sie Ihre persönlichen Daten
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="bg-card border border-border p-6">
            <div className="text-center">
              <div className="w-24 h-24 bg-secondary flex items-center justify-center mx-auto mb-4">
                <span className="font-['Oswald'] text-3xl font-bold text-foreground">
                  {getInitials(user?.name)}
                </span>
              </div>
              <h2 className="font-['Oswald'] text-xl font-bold text-foreground uppercase">
                {user?.name}
              </h2>
              <p className="text-muted-foreground text-sm">{user?.email}</p>
            </div>
            
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                Änderungen werden sofort für alle Mitglieder sichtbar. Der Admin wird über Änderungen informiert.
              </p>
            </div>
          </div>

          {/* Edit Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-card border border-border p-6">
              <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight mb-6">
                Persönliche Daten
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(formData).map(([fieldName, value]) => (
                  <div key={fieldName} className="space-y-2">
                    <Label 
                      htmlFor={fieldName}
                      className="text-xs uppercase tracking-wider text-muted-foreground"
                    >
                      {fieldName}
                    </Label>
                    <Input
                      id={fieldName}
                      type={fieldName.toLowerCase().includes('email') ? 'email' : 'text'}
                      value={value}
                      onChange={(e) => handleChange(fieldName, e.target.value)}
                      className={`bg-secondary/50 border-border rounded-none ${
                        value !== originalData[fieldName] ? 'border-yellow-500' : ''
                      }`}
                      data-testid={`field-${fieldName}`}
                    />
                    {value !== originalData[fieldName] && (
                      <p className="text-xs text-yellow-500">
                        Geändert (war: {originalData[fieldName] || '—'})
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex gap-4">
                <Button
                  type="submit"
                  disabled={saving || !hasChanges()}
                  className="bg-primary hover:bg-red-700 text-foreground uppercase tracking-widest font-bold text-sm rounded-none btn-glow disabled:opacity-50"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="mr-2" size={18} />
                      Änderungen speichern
                    </>
                  )}
                </Button>
                
                {hasChanges() && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setFormData({...originalData})}
                    className="text-muted-foreground hover:text-foreground rounded-none"
                  >
                    Zurücksetzen
                  </Button>
                )}
              </div>
            </form>

            {/* Password Change Form */}
            <form onSubmit={handlePasswordChange} className="bg-card border border-border p-6 mt-6">
              <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight mb-6 flex items-center gap-2">
                <Lock size={20} />
                Passwort ändern
              </h3>
              
              <div className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label 
                    htmlFor="currentPassword"
                    className="text-xs uppercase tracking-wider text-muted-foreground"
                  >
                    Aktuelles Passwort
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="bg-secondary/50 border-border rounded-none pr-10"
                      data-testid="current-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label 
                    htmlFor="newPassword"
                    className="text-xs uppercase tracking-wider text-muted-foreground"
                  >
                    Neues Passwort
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="bg-secondary/50 border-border rounded-none pr-10"
                      data-testid="new-password"
                      minLength={6}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Mindestens 6 Zeichen</p>
                </div>

                <div className="space-y-2">
                  <Label 
                    htmlFor="confirmPassword"
                    className="text-xs uppercase tracking-wider text-muted-foreground"
                  >
                    Neues Passwort bestätigen
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className={`bg-secondary/50 border-border rounded-none ${
                      passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                        ? 'border-red-500'
                        : ''
                    }`}
                    data-testid="confirm-password"
                    required
                  />
                  {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                    <p className="text-xs text-red-500">Passwörter stimmen nicht überein</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
                  className="bg-primary hover:bg-red-700 text-foreground uppercase tracking-widest font-bold text-sm rounded-none btn-glow disabled:opacity-50 mt-4"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Lock className="mr-2" size={18} />
                      Passwort ändern
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Newsletter Subscription */}
            <div className="bg-card border border-border p-6 mt-6">
              <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight mb-6 flex items-center gap-2">
                <Bell size={20} />
                Newsletter-Einstellungen
              </h3>
              
              <div className="flex items-center justify-between max-w-md">
                <div className="space-y-1">
                  <p className="font-medium text-foreground">Allgemeiner Newsletter</p>
                  <p className="text-sm text-muted-foreground">
                    Erhalten Sie Neuigkeiten und Updates vom SHRT
                  </p>
                </div>
                
                {loadingNewsletter ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <div className="flex items-center gap-2">
                    {savingNewsletter && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    <Switch
                      checked={newsletterSubscribed}
                      onCheckedChange={handleNewsletterChange}
                      disabled={savingNewsletter}
                      data-testid="newsletter-toggle"
                    />
                  </div>
                )}
              </div>
              
              <p className="mt-4 text-xs text-muted-foreground">
                Hinweis: Event-Benachrichtigungen für angemeldete Events erhalten Sie unabhängig von dieser Einstellung.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
